import * as Sentry from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import AppError from 'lib/AppError';
// REDUX
import { store } from 'redux/store';
import { fetchSavedSearches } from 'redux/actions/thunks/SavedSearchesThunks';
import { fetchFavorites } from 'redux/actions/thunks/FavoritesThunks';
// AUTH & STORE
import AppAuth from 'AppAuth';
import AppStore from 'fetch/AppFetch';
// LIBS
import { heapIdentify } from 'lib/heapHelpers';
import Helper from 'lib/Helper';

/**
 * Hook to fetch and cache user information, set analytics/side-effects, and handle logout on error
 * @param shouldInitialize whether initialization should proceed
 */
export function useUserAuth(shouldInitialize: boolean) {
  const query = useQuery({
    queryKey: ['userInfo'],
    queryFn: async () => {
      await AppAuth.getUserInfo();
      return AppStore.fetchUserInfo();
    },
    enabled: shouldInitialize,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes

    // Retry up to 3 times for 401 errors
    retry: (failureCount: number, error: unknown) => {
      // Define error type for statusCode and message
      const err = error as { statusCode?: number; message?: string };
      const status = err.statusCode ?? parseInt(err.message ?? '', 10) ?? 0;
      return status === 401 && failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * attempt, 3000),
  });

  // If query errors after retries, throw to be caught by ErrorBoundary
  if (query.isError) {
    const errObj = query.error as { statusCode?: number; message?: string };
    const status = errObj.statusCode ?? parseInt(errObj.message ?? '', 10) ?? 0;
    // @ts-ignore: allow numeric statusCode parameter
    throw new AppError(
      status === 401
        ? 'Login failed after multiple attempts. Please contact support.'
        : 'An unexpected authentication error occurred.',
      // @ts-ignore: allow numeric statusCode
      500,
      { originalError: query.error, component: 'useUserAuth', timestamp: new Date().toISOString() },
      (errObj.message as string) || 'Unknown error'
    );
  }

  useEffect(() => {
    if (query.isSuccess && query.data) {
      Sentry.setUser(Helper.getSentryUserContext(query.data));
      heapIdentify(query.data);
      // Load saved searches and favorites
      store.dispatch(fetchSavedSearches());
      store.dispatch(fetchFavorites());
    }
  }, [query.isSuccess, query.data]);

  return query;
}
